/**
 * Pure geometry for a panel that hangs below a header button - the notification
 * panel (KAN-592) and the help menu (KAN-619).
 *
 * THE DEFECT THIS EXISTS TO KILL. Both used to be DaisyUI `dropdown-end`
 * `dropdown-content` panels with a fixed width (`w-80`, `w-56`). `dropdown-end`
 * right-anchors the panel to the TRIGGER's right edge, so the panel's left edge
 * lands at `triggerRight - panelWidth`. Neither button sits at the right edge of
 * the header: `<app-organization-switcher>` carries `order-last grow basis-44`
 * in `main-layout.component.html`, so at wider phone widths it sits BESIDE the
 * icons and pushes them left. `triggerRight - panelWidth` goes negative, and left
 * overflow creates no horizontal page scroll, so the app-shell overflow guard
 * (`e2e/tests/app-shell-horizontal-overflow.spec.ts`) cannot see it.
 *
 * TWO FAULTS, TWO FIXES, BOTH REQUIRED:
 *   1. A fixed width has no viewport relation. Capped here to
 *      `viewport.width - 2*margin`.
 *   2. A width cap ALONE is not enough: a capped panel right-anchored to a button
 *      at x=200 still starts off screen. The LEFT EDGE is clamped to the viewport.
 *
 * Pure and DOM-free on purpose. jsdom has no layout engine, so a
 * `getBoundingClientRect()` assertion in a component spec is a zero rect and can
 * never fail. Doing the arithmetic here means the numbers in the specs are real
 * numbers and a broken clamp genuinely goes red. Real-browser guards:
 * `e2e/tests/notification-panel-viewport-clamp.spec.ts` and
 * `e2e/tests/help-menu-viewport-clamp.spec.ts`.
 */

export interface TriggerRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface AnchoredPanelPlacement {
  /** Viewport-relative `top`, in px, for a position: fixed panel. */
  top: number;
  /** Viewport-relative `left`, in px. Never less than `margin`. */
  left: number;
  /** Panel width in px - capped so it can always fit inside the viewport. */
  width: number;
  /** Cap so a tall panel cannot run off the bottom of a short phone screen. */
  maxHeight: number;
}

/** Gap between the trigger and the panel, and the minimum padding kept from any viewport edge. */
export const ANCHORED_PANEL_MARGIN = 8;

/**
 * Places the panel below the trigger, right-anchored to it (preserving the
 * original `dropdown-end` look wherever there is room for it), then clamps it so
 * it is ALWAYS fully inside the viewport regardless of where in the header the
 * trigger happens to sit.
 */
export function computeAnchoredPanelPlacement(
  trigger: TriggerRect,
  viewport: Viewport,
  options: { preferredWidth: number; margin?: number },
): AnchoredPanelPlacement {
  const margin = options.margin ?? ANCHORED_PANEL_MARGIN;

  // Fault 1: never wider than the viewport can hold.
  const width = Math.max(0, Math.min(options.preferredWidth, viewport.width - margin * 2));

  const top = Math.max(margin, trigger.top + trigger.height + margin);

  // Fault 2: right-anchor to the trigger, then clamp the LEFT edge into the
  // viewport. `maxLeft` uses Math.max(margin, ...) so a panel wider than the
  // viewport (impossible after the cap above, but cheap to keep correct)
  // degrades to flush-left rather than to a negative bound.
  const rightAligned = trigger.left + trigger.width - width;
  const maxLeft = Math.max(margin, viewport.width - margin - width);
  const left = Math.min(Math.max(rightAligned, margin), maxLeft);

  const maxHeight = Math.max(0, viewport.height - top - margin);

  return { top, left, width, maxHeight };
}
